import { ClockCircleOutlined, EllipsisOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, DatePicker, Dropdown, Input, Menu, Modal, Space, Switch, Table, Tooltip, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Integration, IntegrationExecution } from '../../../models';
import { apiClient, apiErrorMessage, getApiBaseUrl } from '../../../utils';
import { RecordHistoryPage } from './record-history-page';
import moment from 'moment';
import { ExecutionRecordsPage } from './execution-records-page';

export function IntegrationDataGrid(props: { integration?: Integration }) {
  const [loading, setLoading] = useState(false);
  const [tableColumns, setTableColumns] = useState<any[]>();
  const [records, setRecords] = useState<any[]>();
  const [historyRecordId, setHistoryRecordId] = useState<string>();
  const [recordsModalOpen, setRecordsModalOpen] = useState<IntegrationExecution>();

  const [tableCurrent, setTableCurrent] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);

  const [search, setSearch] = useState<string>();
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();
  const [showAllRevisions, setShowAllRevisions] = useState(false);
  const [allRevisionsSwitchDisabled, setAllRevisionsSwitchDisabled] = useState(false);

  const { Text } = Typography;
  const { RangePicker } = DatePicker;

  const openHistoryModal = (id: string) => {
    setHistoryRecordId(id);
  };

  const closeHistoryModal = () => {
    setHistoryRecordId(undefined);
  };

  const onSearch = (input: string) => {
    input = input.trim();
    setTableCurrent(1);
    setSearch(input === '' ? undefined : input);
  };

  const onShowAllRevisionsChange = (value: boolean) => {
    setTableCurrent(1);
    setShowAllRevisions(value);
  };

  const onDateRangeChange = (values: any) => {
    setTableCurrent(1);
    if (values) {
      setShowAllRevisions(true);
      setAllRevisionsSwitchDisabled(true);
      setFrom(values[0].format('yyyy-MM-DD'));
      setTo(values[1].format('yyyy-MM-DD'));
    } else {
      setAllRevisionsSwitchDisabled(false);
      setFrom(undefined);
      setTo(undefined);
    }
  };

  const openRecordsModal = (integrationExecution: IntegrationExecution) => {
    setRecordsModalOpen(integrationExecution);
  };

  const closeRecordsModal = () => {
    setRecordsModalOpen(undefined);
  };

  const loadRecords = useCallback(() => {
    setLoading(true);
    let offset = (tableCurrent - 1) * tablePageSize;
    const queryParams = [`limit=${tablePageSize}`, `offset=${offset}`];
    if (search) {
      queryParams.push(`search=${search}`);
    }
    if (from) {
      queryParams.push(`from=${from}`);
    }
    if (to) {
      queryParams.push(`to=${to}`);
    }
    if (showAllRevisions) {
      queryParams.push('showAllRevisions=true');
    }
    const queryParamsStr = queryParams.join('&');
    apiClient
      .get(`integrations/${props.integration?.id}/records?${queryParamsStr}`)
      .then(response => {
        const responseData = response.data.rows as any[];

        if (!responseData.length) {
          setRecords(undefined);
          setTableTotal(0);
          return;
        }

        const responseRecords = responseData.map(r => {
          return {
            MDM_RECORD_ID: r.id,
            key: r.referenceId,
            integrationExecution: r.integrationExecution,
            isPrime: r.isPrime,
            ...r.recordObject,
          };
        });

        setRecords(responseRecords);
        setTableTotal(response.data.count);

        const columns: any[] = [
          {
            title: '',
            key: 'action',
            render: (_: any, record: any) => (
              <div>
                <Button type="link" icon={<ClockCircleOutlined />} onClick={() => openHistoryModal(record.MDM_RECORD_ID)} />
              </div>
            ),
          },
          {
            title: '',
            key: 'isPrime',
            dataIndex: 'isPrime',
            render: (isPrime: boolean) => (
              <>
                {!isPrime && (
                  <Tooltip title="Daha güncel hali mevcut">
                    <Text type="warning">
                      <InfoCircleOutlined />
                    </Text>
                  </Tooltip>
                )}
              </>
            ),
          },
          {
            title:'',
            key:'executionError',
            dataIndex:'integrationExecution',
            render (execution: IntegrationExecution) {
              return execution?.hasError ? (
                <Tooltip title="Aktarım hatası mevcut">
                  <Text type="danger">
                    <WarningOutlined />
                  </Text>
                </Tooltip>
              ) : null;
            }
          },
          {
            title: 'Aktarım',
            key: 'integrationExecution',
            dataIndex: 'integrationExecution',
            render: (execution: IntegrationExecution) => (
              <Button type="link" style={{ paddingLeft: 0, paddingRight: 0 }} onClick={() => openRecordsModal(execution)}>
                {moment(execution?.createdAt).format('DD.MM.yyyy HH:mm:ss')}
              </Button>
            ),
          },
        ];
        const columnNames = Object.getOwnPropertyNames(responseRecords[0]);
        for (let i = 4; i < columnNames.length; i++) {
          const column = {
            title: columnNames[i],
            dataIndex: columnNames[i],
            key: columnNames[i],
            render: (value: any) => value?.toString(),
          };
          columns.push(column);
        }

        const recordExportIdColumnIndex = columns.findIndex(c => c.dataIndex === '__record_export_id');
        if (recordExportIdColumnIndex > -1) {
          const recordExportIdColumn = columns.splice(recordExportIdColumnIndex, 1);
          recordExportIdColumn[0].title= 'Aktarım ID';
          columns.push(recordExportIdColumn[0]);
        }

        setTableColumns(columns);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [Text, from, props.integration?.id, search, showAllRevisions, tableCurrent, tablePageSize, to]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const onActionsClick = (e: any) => {
    if (e.key === 'excelExport') {
      redirectToExcelExportTab();
    }
  };

  const redirectToExcelExportTab = () => {
    const queryParams: string[] = [];
    if (search) {
      queryParams.push(`search=${search}`);
    }
    if (from) {
      queryParams.push(`from=${from}`);
    }
    if (to) {
      queryParams.push(`to=${to}`);
    }
    if (showAllRevisions) {
      queryParams.push('showAllRevisions=true');
    }
    const queryParamsStr = queryParams.join('&');

    const baseApiUrl = getApiBaseUrl() + '/api/';
    const url = `${baseApiUrl}integrations/${props.integration?.id}/records/export?${queryParamsStr}`;
    window.open(url, '_blank');
  };

  const onTableChange = (pagination: any) => {
    setTableCurrent(pagination.current);
    setTablePageSize(pagination.pageSize);
  };

  if (!tableColumns) {
    return null;
  }

  return (
    <>
      <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
        <Space size={32}>
          <Input.Search placeholder="Arama" allowClear={true} onSearch={onSearch} style={{ width: 200 }} />
          <RangePicker onChange={onDateRangeChange} />
          <Space>
            <Switch checked={showAllRevisions} disabled={allRevisionsSwitchDisabled} onChange={onShowAllRevisionsChange} />
            <span>Tüm revizyonları göster</span>
          </Space>
          <Dropdown
            key="more"
            overlay={
              <Menu onClick={onActionsClick}>
                <Menu.Item key="excelExport">Excel'e aktar</Menu.Item>
              </Menu>
            }
          >
            <Button type="text" style={{ padding: '0.25rem' }}>
              <EllipsisOutlined style={{ fontSize: '1.25rem', verticalAlign: 'top' }} />
            </Button>
          </Dropdown>
        </Space>
      </div>
      <Table
        columns={tableColumns}
        dataSource={records}
        loading={loading}
        scroll={{ x: '100%' }}
        pagination={{
          current: tableCurrent,
          total: tableTotal,
          pageSize: tablePageSize,
          pageSizeOptions: ['10', '25', '50', '100'],
          position: ['bottomCenter'],
          showTotal: (total, range) => `Toplam ${total} kayıt. ${range[0]}-${range[1]} arası gösteriliyor.`,
          showSizeChanger: true,
        }}
        onChange={onTableChange}
      />
      <Modal
        title="Kayıt geçmişi"
        visible={historyRecordId !== undefined}
        footer={null}
        onCancel={closeHistoryModal}
        destroyOnClose={true}
        width={'90%'}
      >
        <RecordHistoryPage recordId={historyRecordId} integrationType={props.integration?.integrationType} />
      </Modal>
      <Modal
        title="Aktarım kayıtları"
        visible={!!recordsModalOpen}
        footer={null}
        onCancel={closeRecordsModal}
        destroyOnClose={true}
        width={'90%'}
      >
        <ExecutionRecordsPage integrationExecution={recordsModalOpen} />
      </Modal>
    </>
  );
}
